import { Button } from "@mantine/core";
import ReactDOM from "react-dom";
import { useForm, type SubmitHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { usePersons } from "../../../hooks/usePersons";
import { useToast } from "../../../mantine/notifications/useToast";
import { isApiError } from "../../../utils/isApiError";
import { AnchorLink } from "../../AnchorLink/AnchorLink";
import { FormFieldController } from "../../BuildingBlocks/Forms/Controllers/FormFieldController";
import type { NumericComboboxItem } from "../../BuildingBlocks/Forms/MantineFormFields/fields/FormFieldSelect";
import { setErrorsFromResponseData } from "../../BuildingBlocks/Forms/utils/setErrorsFromResponseData";
import { LoadOrError } from "../../LoadOrError/LoadOrError";
import { useMaStRGeneratorMutation } from "./hooks/useMaStRGeneratorMutation";
import {
  MASTR_GENERATOR_DEFAULT_VALUES,
  MASTR_GENERATOR_FORM_FIELD_DATA,
  type MaStRGeneratorFormValues
} from "./MaStRSubStepGenerator.constants";

interface MaStRSubStepGeneratorProps {
  buttonContainer?: HTMLElement;
  siteId: number;
  variantId: number;
  onSubmit: (taskId: string) => void;
  onBack: () => void;
}

function MaStRSubStepGenerator({
  buttonContainer,
  siteId,
  variantId,
  onSubmit,
  onBack
}: MaStRSubStepGeneratorProps) {
  const { t } = useTranslation();
  const { showErrorToast } = useToast();
  const { persons, isLoading, error } = usePersons({ variantId });
  const mastrGeneratorMutation = useMaStRGeneratorMutation();

  const {
    control,
    formState: { errors },
    handleSubmit,
    setError,
    watch
  } = useForm<MaStRGeneratorFormValues>({
    defaultValues: MASTR_GENERATOR_DEFAULT_VALUES
  });

  const personChoices: Array<NumericComboboxItem> = persons.map((person) => ({
    value: person.id,
    label: person.name
  }));

  const onValidSubmit: SubmitHandler<MaStRGeneratorFormValues> = async (
    data
  ) => {
    if (data.person === null) {
      return;
    }

    let taskId: string;

    try {
      taskId = await mastrGeneratorMutation.mutateAsync({
        mastr_nummer: data.mastrNummer,
        site: siteId,
        person: data.person
      });
    } catch (error) {
      if (isApiError(error) && error.response?.status === 400) {
        setErrorsFromResponseData<MaStRGeneratorFormValues>(
          showErrorToast,
          error,
          watch(),
          setError,
          t("errors.UnknownError")
        );
      } else {
        showErrorToast(error);
      }

      return;
    }

    onSubmit(taskId);
  };

  const submitCallback = handleSubmit(onValidSubmit);

  const buttons = (
    <>
      <Button
        disabled={mastrGeneratorMutation.isPending}
        variant="default"
        onClick={onBack}
      >
        Zurück
      </Button>
      <Button
        color="secondary"
        loading={mastrGeneratorMutation.isPending}
        onClick={submitCallback}
      >
        Weiter
      </Button>
    </>
  );

  return (
    <div className="mastr-substep">
      <GeneratorExplanation />
      <LoadOrError error={error} loading={isLoading}>
        <form noValidate onSubmit={submitCallback}>
          <FormFieldController
            control={control}
            data={MASTR_GENERATOR_FORM_FIELD_DATA.mastrNummer}
            error={errors.mastrNummer}
          />
          <FormFieldController
            control={control}
            data={{
              ...MASTR_GENERATOR_FORM_FIELD_DATA.person,
              data: personChoices
            }}
            error={errors.person}
          />
        </form>
      </LoadOrError>
      {buttonContainer
        ? ReactDOM.createPortal(buttons, buttonContainer)
        : buttons}
    </div>
  );
}

function GeneratorExplanation() {
  return (
    <p className="explanation">
      Bitte geben Sie die Marktstammdatenregisternummer einer Einheit oder
      Anlage ein und wählen Sie den Betreiber. Es werden automatisch alle
      zusammengehörigen Einheiten und Anlagen angelegt. Eine{" "}
      <AnchorLink href="https://www.marktstammdatenregister.de/MaStR/Einheit/Einheiten/OeffentlicheEinheitenuebersicht">
        Liste öffentlicher Einheiten
      </AnchorLink>{" "}
      ist online verfügbar.
    </p>
  );
}

export { MaStRSubStepGenerator, type MaStRSubStepGeneratorProps };
